import { useState } from "react";
import { useNavigate } from "react-router-dom";

const BRAND = "#FF6B35";

type MsgType = "order" | "system" | "activity" | "service";

interface Message {
  id: string;
  type: MsgType;
  title: string;
  preview: string;
  time: string;
  unread: boolean;
  icon: string;
  iconBg: string;
  iconColor: string;
  path?: string;
  tag?: string;
  tagColor?: string;
  tagBg?: string;
}

const messages: Message[] = [
  {
    id: "m001",
    type: "order",
    title: "订单状态更新",
    preview: "您的三亚5天套餐订单（#M20250413001）已进入「待出行」状态，请提前做好出行准备。",
    time: "刚刚",
    unread: true,
    icon: "ri-plane-line",
    iconBg: "#FFF3EE",
    iconColor: BRAND,
    path: "/orders",
    tag: "订单通知",
    tagColor: BRAND,
    tagBg: "#FFF3EE",
  },
  {
    id: "m002",
    type: "service",
    title: "管家专属跟进",
    preview: "您好！我是您的专属旅行管家王晓芸，您的三亚行程已全部确认，随时可联系我协助安排。",
    time: "10分钟前",
    unread: true,
    icon: "ri-customer-service-2-line",
    iconBg: "#EFF6FF",
    iconColor: "#2563EB",
    tag: "管家消息",
    tagColor: "#2563EB",
    tagBg: "#EFF6FF",
  },
  {
    id: "m003",
    type: "order",
    title: "合同签署提醒",
    preview: "您的「三亚5日亲子深度游」订单（#S20250408003）尚未签署合同，请尽快完成签约以保障您的出行权益。",
    time: "1小时前",
    unread: true,
    icon: "ri-file-paper-2-line",
    iconBg: "#E0F2FE",
    iconColor: "#0EA5E9",
    path: "/orders",
    tag: "待办提醒",
    tagColor: "#0EA5E9",
    tagBg: "#E0F2FE",
  },
  {
    id: "m004",
    type: "activity",
    title: "五一特惠活动",
    preview: "五一假期特惠套餐开抢！三亚海景酒店低至¥688起，限时48小时，先到先得。",
    time: "2小时前",
    unread: false,
    icon: "ri-coupon-3-line",
    iconBg: "#FEF9EE",
    iconColor: "#D97706",
    path: "/hotels",
    tag: "优惠活动",
    tagColor: "#D97706",
    tagBg: "#FEF9EE",
  },
  {
    id: "m005",
    type: "order",
    title: "付款成功确认",
    preview: "您已成功支付订单 #S20250412001 订金 ¥137.60，感谢您的信任，管家将在24小时内与您联系。",
    time: "昨天 14:23",
    unread: false,
    icon: "ri-secure-payment-line",
    iconBg: "#ECFDF5",
    iconColor: "#059669",
    path: "/orders",
    tag: "支付通知",
    tagColor: "#059669",
    tagBg: "#ECFDF5",
  },
  {
    id: "m006",
    type: "system",
    title: "实名认证提醒",
    preview: "您的账号尚未完成实名认证，完成认证后可解锁更多专属服务权益，点击立即认证。",
    time: "昨天 10:05",
    unread: false,
    icon: "ri-id-card-line",
    iconBg: "#F5F3FF",
    iconColor: "#7C3AED",
    path: "/profile",
    tag: "系统提醒",
    tagColor: "#7C3AED",
    tagBg: "#F5F3FF",
  },
  {
    id: "m007",
    type: "order",
    title: "导游已确认服务",
    preview: "导游陈美玲已确认接单，将于2025年5月1日为您提供全程陪同服务，期待与您的旅行！",
    time: "2天前",
    unread: false,
    icon: "ri-user-star-line",
    iconBg: "#F5F3FF",
    iconColor: "#7C3AED",
    path: "/orders",
    tag: "订单通知",
    tagColor: BRAND,
    tagBg: "#FFF3EE",
  },
  {
    id: "m008",
    type: "activity",
    title: "积分到账通知",
    preview: "您本次消费获得积分 +800，当前积分余额：3,580分，可兑换优惠券或抵扣现金使用。",
    time: "3天前",
    unread: false,
    icon: "ri-medal-line",
    iconBg: "#FEF9EE",
    iconColor: "#D97706",
    tag: "积分通知",
    tagColor: "#D97706",
    tagBg: "#FEF9EE",
  },
  {
    id: "m009",
    type: "system",
    title: "美伊平台使用指南",
    preview: "欢迎使用美伊旅行！通过AI助手，您可以一句话完成旅行规划，涵盖酒店、租车、线路和导游预订。",
    time: "5天前",
    unread: false,
    icon: "ri-sparkling-2-line",
    iconBg: "#FFF3EE",
    iconColor: BRAND,
    path: "/ai-plan",
    tag: "使用指南",
    tagColor: "#6B7280",
    tagBg: "#F3F4F6",
  },
];

const typeFilters: { label: string; types?: MsgType[] }[] = [
  { label: "全部" },
  { label: "订单", types: ["order"] },
  { label: "管家", types: ["service"] },
  { label: "活动", types: ["activity"] },
  { label: "系统", types: ["system"] },
];

export default function MessagesPage() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState(0);
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [msgs, setMsgs] = useState<Message[]>(messages);

  const filtered = activeFilter === 0
    ? msgs
    : msgs.filter(m => typeFilters[activeFilter].types?.includes(m.type));

  const unreadCount = msgs.filter(m => m.unread && !readIds.has(m.id)).length;

  const markRead = (id: string) => setReadIds(prev => new Set([...prev, id]));

  const markAllRead = () => {
    setMsgs(prev => prev.map(m => ({ ...m, unread: false })));
    setReadIds(new Set());
  };

  const isUnread = (m: Message) => m.unread && !readIds.has(m.id);

  return (
    <div className="min-h-screen pb-8" style={{ background: "#F7F7F7", fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif" }}>
      {/* Header */}
      <div className="bg-white sticky top-0 z-40" style={{ borderBottom: "1px solid #F0F0F0" }}>
        <div className="px-4 pb-0"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)" }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="w-9 h-9 flex items-center justify-center rounded-xl cursor-pointer"
                style={{ background: "#F5F5F5" }}
              >
                <i className="ri-arrow-left-line text-gray-700" style={{ fontSize: 18 }} />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-lg font-bold text-gray-900">消息中心</h1>
                  {unreadCount > 0 && (
                    <span
                      className="text-xs font-bold px-2 py-0.5 rounded-full text-white"
                      style={{ background: "#EF4444" }}
                    >
                      {unreadCount}条未读
                    </span>
                  )}
                </div>
              </div>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs font-medium cursor-pointer whitespace-nowrap"
                style={{ color: BRAND }}
              >
                全部已读
              </button>
            )}
          </div>

          {/* 分类过滤 */}
          <div className="flex gap-5 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
            {typeFilters.map((f, i) => (
              <button
                key={f.label}
                onClick={() => setActiveFilter(i)}
                className="pb-3 text-sm cursor-pointer relative whitespace-nowrap flex-shrink-0"
                style={{ color: activeFilter === i ? BRAND : "#ACACAC", fontWeight: activeFilter === i ? 700 : 400 }}
              >
                {f.label}
                {activeFilter === i && (
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full" style={{ height: 2.5, width: 18, background: BRAND }} />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Message list */}
      <div className="px-4 py-3 space-y-2">
        {filtered.map(msg => (
          <div
            key={msg.id}
            className="bg-white rounded-2xl px-4 py-4 cursor-pointer active:scale-[0.99] transition-all relative"
            style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}
            onClick={() => {
              markRead(msg.id);
              if (msg.path) navigate(msg.path);
            }}
          >
            {/* 未读红点 */}
            {isUnread(msg) && (
              <div
                className="absolute top-4 right-4 w-2 h-2 rounded-full"
                style={{ background: "#EF4444" }}
              />
            )}
            <div className="flex items-start gap-3">
              {/* Icon */}
              <div
                className="w-11 h-11 flex items-center justify-center rounded-2xl flex-shrink-0"
                style={{ background: msg.iconBg }}
              >
                <i className={msg.icon} style={{ color: msg.iconColor, fontSize: 20 }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-bold text-gray-900 flex-1 min-w-0 truncate">{msg.title}</span>
                  {msg.tag && (
                    <span
                      className="text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0"
                      style={{ background: msg.tagBg, color: msg.tagColor }}
                    >
                      {msg.tag}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-1.5">{msg.preview}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-300">{msg.time}</span>
                  {msg.path && (
                    <span className="text-xs flex items-center gap-0.5" style={{ color: BRAND }}>
                      查看详情<i className="ri-arrow-right-s-line" style={{ fontSize: 13 }} />
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}
